import { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  COLOR_BY_STATUS,
  COMPANY_EXPENSE_ID,
  ClaimStatus,
  useClaim,
  useUpdateClaim,
} from 'store/claims';
import {
  PageHeaderLayout,
  Footer,
  NothingFoundAlert,
  Typography,
  Panel,
  ImageZoom,
  Loader,
  Button,
  ExternalLink,
  Link,
} from 'melp-design/components';
import {
  Box,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  Stack,
} from '@mui/material';
import { ROUTES } from 'config';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatCurrency } from 'utils/format';
import { useDefaultCurrency } from 'state/Administrators';
import { useModalContext } from 'store/modal';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { usePredefinedToasts } from 'utils/Toast';
import { ClaimRejectModal } from './atoms';

export const ClaimDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { currency } = useDefaultCurrency();

  const { data: claim, isLoading } = useClaim(id);
  const { mutate: updateClaim, isLoading: isUpdatingClaim } = useUpdateClaim();

  if (isLoading) {
    return <Loader />;
  }

  if (!claim) {
    return <NothingFoundAlert />;
  }

  const FOOTER_BY_STATUS: Record<ClaimStatus, ReactNode> = {
    approved: null,
    paid: null,
    rejected: null,
    pendingEmployeeAction: (
      <Footer>
        <Stack direction="row" justifyContent="end" gap="20px">
          <Button
            label={t('global.actions.reject')}
            variant="danger-outline"
            onClick={async () => {
              const response = await openModal({
                component: ClaimRejectModal,
                props: { title: claim.supplier, action: 'reject' },
              });

              if (response.action === 'REJECT') {
                updateClaim({
                  claimId: claim.id,
                  status: response.payload.status,
                  adminComment: response.payload.comment,
                });
              }
            }}
            disabled={isUpdatingClaim}
          />
          <Button
            label={t('global.actions.approve_amount', {
              amount: formatCurrency(
                i18n.language,
                claim.requestedAmount,
                currency,
              ),
            })}
            variant="secondary"
            onClick={() => {
              updateClaim(
                { claimId: claim.id, status: 'approved' },
                { onSuccess: () => predefinedToasts.success.updated() },
              );
            }}
            disabled={isUpdatingClaim}
          />
        </Stack>
      </Footer>
    ),
    pendingHRAction: (
      <Footer>
        <Stack direction="row" justifyContent="end" gap="20px">
          <Button
            label={t('claims.actions.request_extra_input')}
            variant="neutral-outline"
            onClick={async () => {
              const response = await openModal({
                component: ClaimRejectModal,
                props: { title: claim.supplier, action: 'request' },
              });

              if (response.action === 'REJECT') {
                updateClaim({
                  claimId: claim.id,
                  status: response.payload.status,
                  adminComment: response.payload.comment,
                });
              }
            }}
            disabled={isUpdatingClaim}
          />

          <Button
            label={t('global.actions.reject')}
            variant="danger-outline"
            onClick={async () => {
              const response = await openModal({
                component: ClaimRejectModal,
                props: { title: claim.supplier, action: 'reject' },
              });

              if (response.action === 'REJECT') {
                updateClaim({
                  claimId: claim.id,
                  status: response.payload.status,
                  adminComment: response.payload.comment,
                });
              }
            }}
            disabled={isUpdatingClaim}
          />

          <Button
            label={t('global.actions.approve_amount', {
              amount: formatCurrency(
                i18n.language,
                claim.requestedAmount,
                currency,
              ),
            })}
            variant="secondary"
            onClick={() => {
              updateClaim({ claimId: claim.id, status: 'approved' });
            }}
            disabled={isUpdatingClaim}
          />
        </Stack>
      </Footer>
    ),
  };

  const DETAILS_ITEMS: Array<{ label: string; value: ReactNode }> = [
    {
      label: t('common.full_name'),
      value: claim.employee ? (
        <Link
          label={claim.employee.fullName}
          to={ROUTES.employees.details.replace(':id', claim.employee.id)}
        />
      ) : null,
    },
    {
      label: t('wallet.employeeWalletFundsSource'),
      value: (
        <List sx={{ py: 0 }}>
          {claim.sources.map((s) => (
            <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
              <Link
                label={s.name}
                to={
                  s.id === RECOGNITION_SHOP_ID
                    ? ROUTES.recognitions.settings
                    : s.id === COMPANY_EXPENSE_ID
                    ? ROUTES.claims.settings
                    : ROUTES.benefits.details.replace(':id', s.id)
                }
              />
            </ListItem>
          ))}
        </List>
      ),
    },
    {
      label: t('marketplaceItems.supplier'),
      value: <Typography>{claim.supplier}</Typography>,
    },
    {
      label: t('claims.table.purchase_date.title'),
      value: <Typography>{moment(claim.purchasedAt).format('l')}</Typography>,
    },
    {
      label: t('claims.table.requested_amount.title'),
      value: (
        <Typography>
          {formatCurrency(i18n.language, claim.requestedAmount, currency)}
        </Typography>
      ),
    },
    ...(claim.status === 'approved'
      ? [
          {
            label: t('claims.table.approved_amount.title'),
            value: (
              <Typography>
                {formatCurrency(i18n.language, claim.requestedAmount, currency)}
              </Typography>
            ),
          },
        ]
      : []),
    {
      label: t('common.status'),
      value: (
        <>
          <Typography
            color={COLOR_BY_STATUS[claim.status]}
            variant="p1"
            style={{ fontWeight: 500 }}
          >
            {t(`claims.status.${claim.status}`)}
          </Typography>
          {['pendingEmployeeAction', 'approved', 'rejected'].includes(
            claim.status,
          ) ? (
            <>
              <Typography color={COLOR_BY_STATUS[claim.status]}>
                {claim.admin.fullName}
              </Typography>
              <Typography color={COLOR_BY_STATUS[claim.status]}>
                {claim.admin.email}
              </Typography>
              <Typography color={COLOR_BY_STATUS[claim.status]}>
                {moment(claim.updatedAt).format('l LT')}
              </Typography>
            </>
          ) : null}
        </>
      ),
    },
    ...(claim.adminComment
      ? [
          {
            label: t('clients.comment'),
            value: <Typography>{claim.adminComment}</Typography>,
          },
        ]
      : []),
  ];

  const claimFiles = {
    images: claim.files.filter((file) => file.mime.startsWith('image')),
    docs: claim.files.filter((file) => !file.mime.startsWith('image')),
  };

  return (
    <>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('claims.title'),
            url: ROUTES.claims.list,
          },
          {
            label: t('claims.details.title.full', {
              interpolation: { escapeValue: false },
              date: moment(claim.claimedAt).format('l LT'),
              user: claim.employee?.fullName,
            }),
          },
        ]}
        under={
          <Typography color={COLOR_BY_STATUS[claim.status]}>
            {t(`claims.status.${claim.status}`)}
          </Typography>
        }
      />

      <Stack gap={2} flexDirection="row">
        <Box width="50%">
          <Panel>
            {claimFiles.docs.map((file) => (
              <Box key={file.id} py={0.5}>
                <ExternalLink label={file.name} href={file.url} />
              </Box>
            ))}
            {claimFiles.images.length ? (
              <ImageList cols={1}>
                {claimFiles.images.map((file) => (
                  <ImageListItem key={file.id}>
                    <ImageZoom src={file.url} alt={file.name} />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : null}
          </Panel>
        </Box>

        <Box width="50%">
          <Panel>
            <Stack spacing={2}>
              {DETAILS_ITEMS.map((item, i) => (
                <Stack gap={2} flexDirection="row" key={i}>
                  <Box width="40%">
                    <Typography color="textSecondary">{item.label}</Typography>
                  </Box>
                  <Box width="60%">{item.value}</Box>
                </Stack>
              ))}
            </Stack>
          </Panel>
        </Box>
      </Stack>

      {FOOTER_BY_STATUS[claim.status]}
    </>
  );
};
